import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { getApiUrl } from '../../lib/api';

interface Strain {
  name: string;
  type?: string;
  thc_content?: string;
  effects?: string;
  flavors?: string;
  description?: string;
}

interface Question {
  text: string;
  options: string[];
  correct: number;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickRandom<T>(arr: T[], n: number): T[] {
  return shuffle(arr).slice(0, n);
}

function generateQuestions(strains: Strain[]): Question[] {
  const valid = strains.filter(s => s.name);
  if (valid.length < 4) return [];

  const questions: Question[] = [];

  // Type: "What type is X?"
  const typed = valid.filter(s => s.type && typeof s.type === 'string' && ['indica', 'sativa', 'hybrid'].includes(s.type.toLowerCase()));
  for (const s of pickRandom(typed, 3)) {
    questions.push({
      text: `What type of strain is "${s.name}"?`,
      options: shuffle(['Indica', 'Sativa', 'Hybrid']),
      correct: -1, // set below
    });
    questions[questions.length - 1].correct = questions[questions.length - 1].options
      .findIndex(o => typeof s.type === 'string' && o.toLowerCase() === s.type.toLowerCase());
  }

  // Effects: "Which strain is known for X effect?"
  const withEffects = valid.filter(s => s.effects && s.effects.length > 2);
  for (const s of pickRandom(withEffects, 3)) {
    const effect = Array.isArray(s.effects) ? s.effects[0] : (s.effects as string).split(',')[0].trim();
    const others = pickRandom(valid.filter(o => o.name !== s.name), 3);
    const opts = shuffle([s.name, ...others.map(o => o.name)]);
    questions.push({
      text: `Which strain is known for "${effect}" effects?`,
      options: opts,
      correct: opts.indexOf(s.name),
    });
  }

  // Flavor: "What flavor does X have?"
  const withFlavors = valid.filter(s => s.flavors && s.flavors.length > 2);
  for (const s of pickRandom(withFlavors, 2)) {
    const flavor = s.flavors!.split(',')[0].trim();
    const otherFlavors = pickRandom(
      withFlavors.filter(o => o.name !== s.name).map(o => o.flavors!.split(',')[0].trim()),
      3
    );
    const opts = shuffle([flavor, ...otherFlavors]);
    questions.push({
      text: `What is the primary flavor of "${s.name}"?`,
      options: opts,
      correct: opts.indexOf(flavor),
    });
  }

  // Description: "Which strain description matches?"
  const withDesc = valid.filter(s => s.description && s.description.length > 20);
  for (const s of pickRandom(withDesc, 2)) {
    const snippet = s.description!.slice(0, 80) + '...';
    const others = pickRandom(valid.filter(o => o.name !== s.name), 3);
    const opts = shuffle([s.name, ...others.map(o => o.name)]);
    questions.push({
      text: `Which strain matches: "${snippet}"`,
      options: opts,
      correct: opts.indexOf(s.name),
    });
  }

  return shuffle(questions).slice(0, 10);
}

type Phase = 'loading' | 'ready' | 'playing' | 'result';

export default function StrainTriviaTab() {
  const [strains, setStrains] = useState<Strain[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [qIndex, setQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [timer, setTimer] = useState(20);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    fetch(getApiUrl('/api/strains-data?per_page=500'))
      .then(r => r.json())
      .then(data => {
        const items = Array.isArray(data) ? data : data.items ?? data.data ?? [];
        setStrains(items);
        setPhase('ready');
      })
      .catch(() => setPhase('ready'));
  }, []);

  const startGame = useCallback(() => {
    const qs = generateQuestions(strains);
    if (qs.length === 0) return;
    setQuestions(qs);
    setQIndex(0);
    setScore(0);
    setAnswered(null);
    setTimer(20);
    setPhase('playing');
  }, [strains]);

  // Timer
  useEffect(() => {
    if (phase !== 'playing' || answered !== null) return;
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setAnswered(-1); // timed out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, answered, qIndex]);

  const handleAnswer = (idx: number) => {
    if (answered !== null) return;
    clearInterval(timerRef.current);
    setAnswered(idx);
    if (idx === questions[qIndex].correct) setScore(s => s + 1);
  };

  const nextQuestion = () => {
    if (qIndex + 1 >= questions.length) {
      setPhase('result');
    } else {
      setQIndex(i => i + 1);
      setAnswered(null);
      setTimer(20);
    }
  };

  const q = questions[qIndex];

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {phase === 'loading' && (
        <div className="text-center py-16 text-white/40">Loading strain data...</div>
      )}

      {phase === 'ready' && (
        <div className="text-center py-12 space-y-4">
          <h3 className="text-white text-xl font-bold">Strain Trivia</h3>
          <p className="text-white/50 text-sm">10 questions about cannabis strains. 20 seconds each.</p>
          <p className="text-white/30 text-xs">{strains.length} strains loaded</p>
          <button
            onClick={startGame}
            disabled={strains.length < 4}
            className="px-6 py-3 bg-[#4ade80]/20 border border-[#4ade80]/40 text-[#4ade80] font-semibold rounded-xl hover:bg-[#4ade80]/30 transition disabled:opacity-30"
          >
            Start Quiz
          </button>
        </div>
      )}

      {phase === 'playing' && q && (
        <motion.div
          key={qIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-5"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-xs">Question {qIndex + 1}/{questions.length}</span>
            <div className="flex items-center gap-2">
              <span className="text-[#4ade80] text-xs font-semibold">Score: {score}</span>
              <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${timer <= 5 ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-white/50'}`}>
                {timer}s
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#4ade80]/60 rounded-full transition-all duration-300"
              style={{ width: `${((qIndex + 1) / questions.length) * 100}%` }}
            />
          </div>

          {/* Question */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-white font-semibold text-sm leading-relaxed">{q.text}</p>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 gap-2">
            {q.options.map((opt, i) => {
              let cls = 'border-white/10 bg-white/[0.02] hover:border-white/20 text-white/70';
              if (answered !== null) {
                if (i === q.correct) cls = 'border-[#4ade80]/40 bg-[#4ade80]/10 text-[#4ade80]';
                else if (i === answered && i !== q.correct) cls = 'border-red-500/40 bg-red-500/10 text-red-400';
                else cls = 'border-white/5 bg-white/[0.01] text-white/30';
              }
              return (
                <button
                  key={i}
                  onClick={() => handleAnswer(i)}
                  disabled={answered !== null}
                  className={`text-left px-4 py-3 rounded-xl border text-sm font-medium transition ${cls}`}
                >
                  <span className="text-white/30 mr-2">{String.fromCharCode(65 + i)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>

          {/* Feedback + Next */}
          {answered !== null && (
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${answered === q.correct ? 'text-[#4ade80]' : 'text-red-400'}`}>
                {answered === -1 ? 'Time\'s up!' : answered === q.correct ? 'Correct!' : 'Wrong!'}
              </span>
              <button
                onClick={nextQuestion}
                className="px-4 py-2 bg-white/5 border border-white/10 text-white/70 rounded-xl text-sm hover:bg-white/10 transition"
              >
                {qIndex + 1 >= questions.length ? 'See Results' : 'Next'}
              </button>
            </div>
          )}
        </motion.div>
      )}

      {phase === 'result' && (
        <div className="text-center py-12 space-y-5">
          <div className="text-5xl font-bold text-[#4ade80]">{score}/{questions.length}</div>
          <p className="text-white/60 text-sm">
            {score >= 8 ? 'Cannabis connoisseur!' : score >= 5 ? 'Not bad, keep learning!' : 'Time to study up!'}
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={startGame}
              className="px-5 py-2.5 bg-[#4ade80]/20 border border-[#4ade80]/40 text-[#4ade80] font-semibold rounded-xl hover:bg-[#4ade80]/30 transition text-sm"
            >
              Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
